import { useMemo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  type DeviceMstpRegion,
  type DeviceStpInconsistency,
  type DeviceStpInstance,
  type DeviceStpInterfaceState,
  useDeviceStp,
} from '@/hooks/useDeviceStp';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

interface DeviceStpTabProps {
  deviceId: string;
}

const KIND_LABELS: Record<string, string> = {
  bpdu_guard_violated: 'BPDU Guard triggered',
  root_guard_violated: 'Root Guard triggered',
  missing_bpdu_guard: 'Missing BPDU Guard',
  missing_root_guard: 'Missing Root Guard',
  loop_guard_violated: 'Loop Guard triggered',
  port_stuck_non_forwarding: 'Port stuck non-forwarding',
};

function severityClass(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-sev-1/15 text-sev-1';
    case 'high':
      return 'bg-sev-1/10 text-sev-1';
    case 'medium':
      return 'bg-sev-3/20 text-foreground';
    case 'low':
      return 'bg-sev-5/10 text-sev-5';
    case 'info':
      return 'bg-sev-5/15 text-sev-5';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function portStateClass(state: string): string {
  switch (state.toLowerCase()) {
    case 'forwarding':
      return 'bg-sev-4/15 text-sev-4';
    case 'learning':
      return 'bg-sev-5/15 text-sev-5';
    case 'discarding':
    case 'blocking':
    case 'listening':
      return 'bg-sev-3/20 text-foreground';
    case 'disabled':
    case 'broken':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function portRoleClass(role: string | null): string {
  if (!role) return 'bg-muted text-muted-foreground';
  switch (role.toLowerCase()) {
    case 'root':
      return 'bg-brand-soft text-brand';
    case 'designated':
      return 'bg-sev-5/15 text-sev-5';
    case 'alternate':
    case 'backup':
      return 'bg-sev-3/20 text-foreground';
    case 'disabled':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function GuardCell({ enabled, violated }: { enabled: boolean; violated: boolean }) {
  if (violated) {
    return (
      <Badge className="bg-sev-1/15 text-sev-1 border-0" title="Guard triggered">
        ⚠
      </Badge>
    );
  }
  if (enabled) {
    return (
      <Badge className="bg-sev-4/15 text-sev-4 border-0" title="Guard configured">
        ✓
      </Badge>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

function formatTimestamp(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function InstancesTable({ instances }: { instances: DeviceStpInstance[] }) {
  if (instances.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No STP instances reported for this device.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border-subtle">
          <tr className="text-xs text-muted-foreground uppercase tracking-wider">
            <th className="text-left py-2 px-3 font-medium">Inst</th>
            <th className="text-left py-2 px-3 font-medium">Variant</th>
            <th className="text-left py-2 px-3 font-medium">Bridge ID</th>
            <th className="text-left py-2 px-3 font-medium">Root ID</th>
            <th className="text-left py-2 px-3 font-medium">Root port</th>
            <th className="text-right py-2 px-3 font-medium">Root cost</th>
            <th className="text-right py-2 px-3 font-medium">TC count</th>
            <th className="text-left py-2 px-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {instances.map((inst) => (
            <tr key={inst.id}>
              <td className="py-2 px-3">
                <strong className="font-mono">{inst.instanceId}</strong>
                {inst.vlanId !== null ? (
                  <span className="text-xs text-muted-foreground ml-1">
                    (VLAN {inst.vlanId})
                  </span>
                ) : null}
              </td>
              <td className="py-2 px-3">
                <Badge variant="secondary" className="font-normal uppercase">
                  {inst.instanceType}
                </Badge>
              </td>
              <td className="py-2 px-3">
                <code className="font-mono text-xs">{inst.bridgeId}</code>
                {inst.isRootBridge ? (
                  <Badge className="bg-brand-soft text-brand border-0 ml-2">root</Badge>
                ) : null}
              </td>
              <td className="py-2 px-3">
                <code className="font-mono text-xs">{inst.rootId}</code>
              </td>
              <td className="py-2 px-3">
                {inst.rootPort ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="py-2 px-3 text-right font-mono">{inst.rootPathCost}</td>
              <td className="py-2 px-3 text-right font-mono">
                {inst.topologyChanges?.toLocaleString() ?? (
                  <span className="text-muted-foreground" title="not reported by this device">
                    —
                  </span>
                )}
              </td>
              <td className="py-2 px-3">
                <Badge
                  className={cn(
                    'border-0 font-normal',
                    inst.operationalStatus === 'up'
                      ? 'bg-sev-4/15 text-sev-4'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {inst.operationalStatus ?? 'unknown'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InterfaceStatesTable({ rows }: { rows: DeviceStpInterfaceState[] }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No per-port STP state"
        description="If this is a managed switch, ensure SNMP is configured and BRIDGE-MIB (.1.3.6.1.2.1.17.2) is reachable — or that an HTTP-backed source is set up in the vendor profile for Arista/Juniper."
        size="inline"
      />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border-subtle">
          <tr className="text-xs text-muted-foreground uppercase tracking-wider">
            <th className="text-left py-2 px-3 font-medium">Inst</th>
            <th className="text-left py-2 px-3 font-medium">Interface</th>
            <th className="text-left py-2 px-3 font-medium">Port #</th>
            <th className="text-left py-2 px-3 font-medium">State</th>
            <th className="text-left py-2 px-3 font-medium">Role</th>
            <th className="text-left py-2 px-3 font-medium">Edge</th>
            <th className="text-right py-2 px-3 font-medium">Cost</th>
            <th className="text-center py-2 px-3 font-medium" title="BPDU Guard">BPDU&nbsp;G</th>
            <th className="text-center py-2 px-3 font-medium" title="Root Guard">Root&nbsp;G</th>
            <th className="text-center py-2 px-3 font-medium" title="Loop Guard">Loop&nbsp;G</th>
            <th className="text-center py-2 px-3 font-medium" title="BPDU Filter">BPDU&nbsp;F</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="py-2 px-3 font-mono text-xs">{row.instanceId}</td>
              <td className="py-2 px-3">
                <strong className="font-mono">{row.interfaceName ?? '—'}</strong>
              </td>
              <td className="py-2 px-3 font-mono text-xs">{row.portNumber}</td>
              <td className="py-2 px-3">
                <Badge className={cn('border-0 font-normal', portStateClass(row.portState))}>
                  {row.portState}
                </Badge>
              </td>
              <td className="py-2 px-3">
                {row.portRole ? (
                  <Badge className={cn('border-0 font-normal', portRoleClass(row.portRole))}>
                    {row.portRole}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="py-2 px-3">
                {row.operEdgePort ? (
                  <Badge variant="secondary" className="font-normal">edge</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="py-2 px-3 text-right font-mono">{row.operPathCost}</td>
              <td className="py-2 px-3 text-center">
                <GuardCell enabled={row.bpduGuardEnabled} violated={row.bpduGuardViolated} />
              </td>
              <td className="py-2 px-3 text-center">
                <GuardCell enabled={row.rootGuardEnabled} violated={row.rootGuardViolated} />
              </td>
              <td className="py-2 px-3 text-center">
                <GuardCell enabled={row.loopGuardEnabled} violated={row.loopGuardViolated} />
              </td>
              <td className="py-2 px-3 text-center">
                {row.bpduFilterEnabled ? (
                  <Badge className="bg-sev-4/15 text-sev-4 border-0">✓</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/// Label-left / value-right row, matching the device-details info layout
/// (DeviceDetails `InfoRow`). Renders a fragment so `<dt>`/`<dd>` flow directly
/// into the parent `grid-cols-[max-content_1fr]` definition list.
function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground font-medium">{label}</dt>
      <dd className="break-words min-w-0">{value}</dd>
    </>
  );
}

function MstpRegionCard({ region }: { region: DeviceMstpRegion }) {
  const byInstance = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const m of region.vlanMappings) {
      const list = map.get(m.instanceId);
      if (list) list.push(m.vlanId);
      else map.set(m.instanceId, [m.vlanId]);
    }
    return Array.from(map.entries())
      .map(([instanceId, vlanIds]) => ({
        instanceId,
        vlanIds: vlanIds.sort((a, b) => a - b),
      }))
      .sort((a, b) => a.instanceId - b.instanceId);
  }, [region.vlanMappings]);

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <h2 className="text-sm font-semibold mb-3">MSTP region</h2>
      <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm mb-4">
        <InfoRow
          label="Region name"
          value={region.regionName || <span className="text-muted-foreground">—</span>}
        />
        <InfoRow label="Revision" value={<span className="font-mono">{region.revisionLevel}</span>} />
        {region.configurationDigest ? (
          <InfoRow
            label="Configuration digest"
            value={<code className="font-mono text-xs">{region.configurationDigest}</code>}
          />
        ) : null}
        {region.cistRootId ? (
          <InfoRow label="CIST root" value={<code className="font-mono text-xs">{region.cistRootId}</code>} />
        ) : null}
        {region.cistRegionalRootId ? (
          <InfoRow
            label="CIST regional root"
            value={<code className="font-mono text-xs">{region.cistRegionalRootId}</code>}
          />
        ) : null}
        {region.cistPathCost !== null ? (
          <InfoRow label="CIST path cost" value={<span className="font-mono">{region.cistPathCost}</span>} />
        ) : null}
      </dl>

      <h3 className="text-sm font-semibold mb-2">VLAN → instance mapping</h3>
      {byInstance.length === 0 ? (
        <p className="text-sm text-muted-foreground">No VLAN mappings collected.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-xs text-muted-foreground uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Inst</th>
                <th className="text-left py-2 px-3 font-medium">VLANs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {byInstance.map(({ instanceId, vlanIds }) => (
                <tr key={instanceId}>
                  <td className="py-2 px-3 font-mono font-medium">{instanceId}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {vlanIds.map((v) => (
                        <Badge key={v} className="bg-sev-5/15 text-sev-5 border-0 font-normal">
                          {v}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function InconsistenciesPanel({ rows }: { rows: DeviceStpInconsistency[] }) {
  if (rows.length === 0) return null;
  return (
    <section className="bg-card border border-sev-3 rounded-lg p-5">
      <h2 className="text-sm font-semibold mb-3 inline-flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-sev-3" />
        Active findings
        <span className="text-xs font-normal text-muted-foreground">
          ({rows.length} {rows.length === 1 ? 'finding' : 'findings'})
        </span>
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border-subtle">
            <tr className="text-xs text-muted-foreground uppercase tracking-wider">
              <th className="text-left py-2 px-3 font-medium">Severity</th>
              <th className="text-left py-2 px-3 font-medium">Kind</th>
              <th className="text-left py-2 px-3 font-medium">Inst</th>
              <th className="text-left py-2 px-3 font-medium">Port</th>
              <th className="text-left py-2 px-3 font-medium">Detail</th>
              <th className="text-left py-2 px-3 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="py-2 px-3">
                  <Badge className={cn('border-0 font-normal capitalize', severityClass(row.severityHint))}>
                    {row.severityHint}
                  </Badge>
                </td>
                <td className="py-2 px-3 font-medium">
                  {KIND_LABELS[row.kind] ?? row.kind}
                </td>
                <td className="py-2 px-3 font-mono text-xs">
                  {row.instanceId ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3 font-mono text-xs">
                  {row.portNumber ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3 text-xs">
                  {row.detail ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 px-3 text-xs text-muted-foreground">
                  {formatTimestamp(row.lastSeenAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function DeviceStpTab({ deviceId }: DeviceStpTabProps) {
  const { data, isLoading, isError, refetch, isFetching } = useDeviceStp(deviceId);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load STP data.</AlertDescription>
      </Alert>
    );
  }

  const noData =
    data.instances.length === 0 &&
    data.interfaceStates.length === 0 &&
    data.inconsistencies.length === 0 &&
    !data.mstpRegion;

  return (
    <div className="space-y-4 md:space-y-5">
      <InconsistenciesPanel rows={data.inconsistencies} />

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">STP instances</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void refetch(); }}
            disabled={isFetching}
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
        {noData ? (
          <EmptyState
            variant="no-data"
            title="No STP data yet"
            description="If this is a managed switch, ensure SNMP is configured and BRIDGE-MIB is reachable. For Arista or Juniper, the vendor profile needs an HTTP-backed STP polling source enabled."
            size="inline"
          />
        ) : (
          <InstancesTable instances={data.instances} />
        )}
      </section>

      {data.mstpRegion ? <MstpRegionCard region={data.mstpRegion} /> : null}

      <section className="bg-card border border-border rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-3">Per-port state</h2>
        <InterfaceStatesTable rows={data.interfaceStates} />
      </section>
    </div>
  );
}
